// RootSumSquareClosedTask.js
// (OTWARTE) – suma / różnica dwóch pierwiastków kwadratowych, np. √72 - √8,
// po uproszczeniu w postaci a * √b.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

/* ===== helpers ===== */

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function coerceToString(val) {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);

  // event z inputa
  if (typeof val === "object" && val.target && "value" in val.target) {
    return String(val.target.value ?? "");
  }

  // SqrtInput / klawiatura latex: { raw: "..." }
  if (typeof val === "object" && "raw" in val) {
    return String(val.raw ?? "");
  }

  // inne popularne klucze
  if (typeof val === "object" && "latex" in val) return String(val.latex ?? "");
  if (typeof val === "object" && "text" in val) return String(val.text ?? "");
  if (typeof val === "object" && "expr" in val) return String(val.expr ?? "");
  if (typeof val === "object" && "value" in val && typeof val.value !== "object") {
    return String(val.value ?? "");
  }

  return "";
}

function normalizeCanonical(str) {
  return String(str ?? "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .replace(/[−–]/g, "-")
    .replace(/\\\\sqrt/g, "√")
    .replace(/\\sqrt/g, "√")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .trim();
}

function evaluateExpression(expr) {
  const cleaned = normalizeCanonical(expr);
  if (!cleaned) return NaN;

  try {
    // 2√3 -> 2*Math.sqrt(3), √12 -> Math.sqrt(12)
    const parsed = cleaned
      .replace(/(\d*)√(\d+)/g, (_, a, b) => `${a || "1"}*Math.sqrt(${b})`)
      .replace(/√(\d+)/g, (_, b) => `Math.sqrt(${b})`);

    return Function(`return ${parsed}`)();
  } catch {
    return NaN;
  }
}

const k = 2;
const SQUAREFREE = [2, 3, 5, 6, 7, 10, 11];

function primeFactors(number) {
  const factors = [];
  let remaining = number;
  for (let prime = 2; prime * prime <= remaining; prime += 1) {
    while (remaining % prime === 0) {
      factors.push(prime);
      remaining /= prime;
    }
  }
  if (remaining > 1) factors.push(remaining);
  return factors;
}

function primeFactorLatex(number) {
  return primeFactors(number).join("\\cdot ");
}

function primeFactorLadderLatex(number) {
  let remaining = number;
  const rows = primeFactors(number).map((prime) => {
    const row = `${remaining} & ${prime} \\\\`;
    remaining /= prime;
    return row;
  });
  rows.push("1 & \\\\");
  return `\\begin{array}{r|l}
${rows.join("\n")}
\\end{array}`;
}

function rootLatex(radicand) {
  return `\\sqrt{${radicand}}`;
}

function coeffTimesRootLatex(coeff, d) {
  const inner = rootLatex(d);
  if (coeff === 1) return inner;
  return `${coeff}${inner}`;
}

/* ===== generator ===== */

export function generateTask() {
  let d, aCoeff, bCoeff, op, A, B, resultCoeff;
  let tries = 0;

  while (tries < 200) {
    tries += 1;

    d = pick(SQUAREFREE);

    const maxCoeff = 6;
    const coeffs = Array.from({ length: maxCoeff - 1 }, (_, i) => i + 2); // 2..6

    aCoeff = pick(coeffs);
    bCoeff = pick(coeffs);
    if (aCoeff === bCoeff) continue;

    op = pick(["+", "-"]);

    if (op === "-") {
      if (aCoeff < bCoeff) [aCoeff, bCoeff] = [bCoeff, aCoeff];
    }

    resultCoeff = op === "+" ? aCoeff + bCoeff : aCoeff - bCoeff;
    if (resultCoeff <= 0) continue;
    if (resultCoeff === 1) continue;

    A = Math.pow(aCoeff, k) * d;
    B = Math.pow(bCoeff, k) * d;
    if (A > 600 || B > 600) continue;

    break;
  }

  const signLatex = op === "-" ? " - " : " + ";
  const exprLatex = `${rootLatex(A)}${signLatex}${rootLatex(B)}`;
  const correctLatex = coeffTimesRootLatex(resultCoeff, d);

  return {
    type: "RootSumSquareClosedTask",
    data: { d, aCoeff, bCoeff, A, B, op, resultCoeff, exprLatex, correctLatex },
    tags: ["dzialania", "pierwiastki", "sqrt"],
  };
}

/* ===== rendering ===== */

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        Oblicz wartość: <InlineMath math={data.exprLatex} />.
      </p>
      <p style={{ opacity: 0.85, marginTop: -6 }}>
        Zapisz wynik w postaci <InlineMath math={"a\\sqrt{b}"} />.
      </p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <SqrtInput
      value={coerceToString(userAnswer)}
      onChange={onChange}
      disabled={disabled}
      wide={true}
      allowCubeRoot={false}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const expected = evaluateExpression(data.correctLatex);
  const given = evaluateExpression(coerceToString(userAnswer));

  if (!Number.isFinite(expected) || !Number.isFinite(given)) return false;
  return Math.abs(expected - given) < 0.01;
}

export function renderAnswer({ data }) {
  return <InlineMath math={data.correctLatex} />;
}

export function renderUserAnswer(userAnswer) {
  const s = coerceToString(userAnswer);
  if (!s || s.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={normalizeCanonical(s)} />;
}

export function renderExplanation({ data }) {
  const { d, aCoeff, bCoeff, A, B, op, resultCoeff, correctLatex } = data;

  const groupedLineA = `${rootLatex(A)}= \\sqrt{${aCoeff}^{2}\\cdot ${d}}= ${coeffTimesRootLatex(
    aCoeff,
    d
  )}`;
  const groupedLineB = `${rootLatex(B)}= \\sqrt{${bCoeff}^{2}\\cdot ${d}}= ${coeffTimesRootLatex(
    bCoeff,
    d
  )}`;
  const combined = `${coeffTimesRootLatex(aCoeff, d)} ${op} ${coeffTimesRootLatex(
    bCoeff,
    d
  )} = ${coeffTimesRootLatex(resultCoeff, d)}`;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1.</strong> Rozkładamy liczby pod pierwiastkami na czynniki
        pierwsze:
      </p>

      <div style={{ display: "flex", gap: 32, flexWrap: "wrap", justifyContent: "center" }}>
        <BlockMath math={primeFactorLadderLatex(A)} />
        <BlockMath math={primeFactorLadderLatex(B)} />
      </div>
      <BlockMath math={`${A}=${primeFactorLatex(A)}`} />
      <BlockMath math={`${B}=${primeFactorLatex(B)}`} />

      <p>
        <strong>Krok 2.</strong> Grupujemy jednakowe czynniki w pary. Z każdej
        pary wyjmujemy jeden czynnik przed pierwiastek:
      </p>

      <BlockMath math={groupedLineA} />
      <BlockMath math={groupedLineB} />

      <p>
        <strong>Krok 3.</strong> Łączymy wyrazy podobne:
      </p>
      <BlockMath math={combined} />

      <p>Odpowiedź:</p>
      <BlockMath math={correctLatex} />
    </div>
  );
}
